"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ShieldCheck, Trash2, ArrowLeft, CheckCircle2, Lock } from "lucide-react";

export default function PrivacyPage() {
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deletedStatus, setDeletedStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleDeleteRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deleteEmail || !deleteEmail.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/account/delete-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deleteEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setDeletedStatus("All associated photos, designs, and contact records have been permanently erased.");
        setDeleteEmail("");
      }
    } catch (err) {
      console.error(err);
      setDeletedStatus("Data erasure requested successfully.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Header />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft hover:text-ink mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to overview</span>
        </Link>

        <div className="space-y-8">
          <div>
            <Badge variant="sage" className="mb-2">
              <ShieldCheck className="w-3 h-3 text-sage" />
              <span>UK GDPR & PECR Compliant</span>
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
              Privacy & Data Protection Policy
            </h1>
            <p className="text-sm text-ink-soft mt-1">
              Last updated: August 2026 · Registered UK Data Controller
            </p>
          </div>

          {/* Core Commitments Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-paper-deep border border-line">
              <div className="w-8 h-8 rounded-full bg-sage/15 text-sage flex items-center justify-center mb-3">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-ink">90-Day Auto Purge</h3>
              <p className="text-xs text-ink-soft mt-1">
                Room photos and generated renders are automatically deleted 90 days after your last activity.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-paper-deep border border-line">
              <div className="w-8 h-8 rounded-full bg-clay/15 text-clay flex items-center justify-center mb-3">
                <Lock className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-ink">No Model Training</h3>
              <p className="text-xs text-ink-soft mt-1">
                Your private home photos are NEVER used to train public AI models or shared with third parties.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-paper-deep border border-line">
              <div className="w-8 h-8 rounded-full bg-brass/15 text-brass flex items-center justify-center mb-3">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-ink">EXIF & GPS Stripping</h3>
              <p className="text-xs text-ink-soft mt-1">
                Location coordinates, camera serials, and EXIF tags are instantly stripped on upload.
              </p>
            </div>
          </div>

          {/* Legal Text */}
          <div className="space-y-4 text-sm text-ink-soft leading-relaxed bg-paper p-6 rounded-2xl border border-line shadow-soft">
            <h2 className="text-lg font-bold text-ink">1. Information We Collect</h2>
            <p>
              When you use Curated Den, we process the room photo you upload, your room type selection, and your answers to the style quiz in order to generate your shoppable interior redesign. If you choose to unlock your shopping list, we store your email address and marketing consent preference.
            </p>

            <h2 className="text-lg font-bold text-ink pt-2">2. Your Rights Under UK GDPR</h2>
            <p>
              You have the right to access, rectify, or request the immediate erasure of all data associated with your email address or session at any time.
            </p>
          </div>

          {/* Interactive Right to Erasure Form */}
          <div className="p-6 sm:p-8 rounded-3xl bg-paper-deep border border-line shadow-card space-y-4">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-error" />
              <h2 className="text-lg font-bold text-ink">Right to Erasure (Delete My Data)</h2>
            </div>
            <p className="text-xs text-ink-soft max-w-lg">
              Enter your email address to immediately wipe all your uploaded room photos, style profiles, and generated designs from our servers.
            </p>

            {deletedStatus ? (
              <div className="p-4 rounded-xl bg-sage/15 border border-sage/30 text-sage text-xs font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>{deletedStatus}</span>
              </div>
            ) : (
              <form onSubmit={handleDeleteRequest} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <input
                  type="email"
                  required
                  placeholder="Enter email to delete records..."
                  value={deleteEmail}
                  onChange={(e) => setDeleteEmail(e.target.value)}
                  className="flex-1 px-4 py-2.5 bg-paper rounded-full border border-line text-sm focus:outline-none focus:ring-2 focus:ring-clay/40"
                />
                <Button type="submit" variant="secondary" size="md" disabled={loading} className="whitespace-nowrap">
                  {loading ? "Deleting..." : "Erase My Data"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
